/**
 * TASK STATUS — helper derivati sullo stato di un task.
 *
 * "completato" è uno stato persistito (TaskStatus.completed). "scaduto" invece
 * NON è persistito: il backend non ricalcola lo status dalle date, quindi lo
 * deriviamo qui dalla expirationDate (solo per visuali/ordinamento, e solo se il
 * task non è già completato).
 */

import type { Task, TaskStatus, TaskTab } from './taskTypes';

export function isTaskCompleted(task: Pick<Task, 'status'>): boolean {
  return task.status === 'completed';
}

export function isTaskExpired(task: Pick<Task, 'status' | 'expirationDate'>): boolean {
  return (
    task.status !== 'completed' &&
    !!task.expirationDate &&
    new Date(task.expirationDate).getTime() < Date.now()
  );
}

/**
 * Finestra "Imminenti": quanti giorni futuri, oltre a oggi, rientrano
 * nell'orizzonte "imminente". Scelta di prodotto TS-48: allargarla o
 * restringerla qui muove sia il tab sia il chip.
 *
 * I due condividono la fine della finestra ma non l'inizio: il chip parte da
 * domani (per oggi c'e' gia' "In scadenza oggi"), il tab dall'istante corrente
 * — vedi {@link taskMatchesTab}.
 */
export const IMMINENT_WINDOW_DAYS = 3;

/**
 * Deriva il "bucket" temporale di un task dalle sue date. Gli stati
 * imminent/expiring_today/expired NON sono persistiti dal backend (che scrive
 * solo TODO/COMPLETED): sono concetti derivati dalla expirationDate, calcolati
 * qui su confini di giorno di calendario locale.
 *
 * Alimenta il CHIP di stato (via {@link taskDisplayStatus}) e il contatore
 * "Task scaduti" della Dashboard. NON i tab della lista: quelli si sovrappongono
 * e hanno un predicato loro ({@link taskMatchesTab}).
 *
 * Ritorna `null` quando il task non ricade in nessun bucket temporale:
 * completato, senza scadenza, o scadenza oltre la finestra imminente (questi
 * compaiono solo nel tab "Tutti"). I bucket sono mutuamente esclusivi — un task
 * ha un chip solo:
 *   - expired:        scadenza < inizio di oggi (giorni precedenti)
 *   - expiring_today: scadenza nella giornata odierna (qualsiasi ora)
 *   - imminent:       scadenza da domani ai prossimi IMMINENT_WINDOW_DAYS giorni
 */
export function taskDateBucket(
  task: Pick<Task, 'status' | 'expirationDate'>,
): 'expired' | 'expiring_today' | 'imminent' | null {
  if (task.status === 'completed' || !task.expirationDate) return null;

  const exp = new Date(task.expirationDate).getTime();

  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const imminentEnd = new Date(startTomorrow);
  imminentEnd.setDate(imminentEnd.getDate() + IMMINENT_WINDOW_DAYS);

  if (exp < startToday.getTime()) return 'expired';
  if (exp < startTomorrow.getTime()) return 'expiring_today';
  if (exp < imminentEnd.getTime()) return 'imminent';
  return null;
}

/**
 * Un task appartiene al tab `tab`? (TS-48)
 *
 * Diversamente da {@link taskDateBucket} questa NON e' una partizione:
 * "Imminente" si sovrappone a "In scadenza oggi" — un task che scade oggi fra
 * un'ora sta in entrambi — quindi i tab non possono derivare dal bucket, che di
 * risposta ne da' una sola per task.
 *
 * "Imminente" e' l'orizzonte da adesso ai prossimi IMMINENT_WINDOW_DAYS giorni.
 * Rispetto al chip omonimo la finestra finisce nello stesso punto ma comincia
 * dall'ISTANTE corrente invece che da domani: prende quindi anche i task di oggi
 * la cui ora non e' ancora passata, e lascia fuori quelli di oggi gia' scaduti.
 *
 * `expired` ed `expiring_today` restano quelli di prima: un task scaduto
 * stamattina risulta ancora "in scadenza oggi" e non "scaduto". E' incoerente
 * con {@link isTaskExpired}, che confronta con l'istante e alimenta timeline e
 * toggle di completamento, ma allinearli e' una decisione di prodotto che qui
 * non e' stata presa.
 */
export function taskMatchesTab(
  task: Pick<Task, 'status' | 'expirationDate'>,
  tab: TaskTab,
): boolean {
  if (tab === 'all') return true;
  if (task.status === 'completed' || !task.expirationDate) return false;

  const exp = new Date(task.expirationDate).getTime();

  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);

  switch (tab) {
    case 'expired':
      return exp < startToday.getTime();
    case 'expiring_today':
      return exp >= startToday.getTime() && exp < startTomorrow.getTime();
    case 'imminent': {
      // L'unico ramo che guarda l'istante invece del confine di giornata: e'
      // cio' che tiene fuori i task di oggi la cui ora e' gia' passata.
      const imminentEnd = new Date(startTomorrow);
      imminentEnd.setDate(imminentEnd.getDate() + IMMINENT_WINDOW_DAYS);
      return exp >= Date.now() && exp < imminentEnd.getTime();
    }
    // 'todo' e 'completed' sono nel tipo TaskTab ma non hanno un tab
    // (vedi TASK_TAB_KEYS in TaskList).
    default:
      return false;
  }
}

/**
 * Stato EFFETTIVO del task per la visualizzazione (chip di stato): usa il
 * bucket temporale derivato dalle date (scaduto/scade oggi/imminente) quando
 * presente, altrimenti lo stato persistito (todo/completed). Fonte unica per
 * il rendering dello stato, coerente coi tab derivati (anche nel tab "Tutti").
 */
export function taskDisplayStatus(task: Pick<Task, 'status' | 'expirationDate'>): TaskStatus {
  return taskDateBucket(task) ?? task.status;
}